"use client";

import { useCallback, useSyncExternalStore } from "react";

export const JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME = "dream:jornaya-lead-id-available";

const DEFAULT_WAIT_TIMEOUT_MS = 3000;
const DEFAULT_WAIT_INTERVAL_MS = 100;

export function getJornayaLeadId(): string | null {
  if (typeof window === "undefined") return null;
  return window.LeadiD?.token || null;
}

export function notifyJornayaLeadIdAvailable() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME));
}

function subscribeToJornaya(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {};

  const handler = () => onStoreChange();
  window.addEventListener(JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME, handler);

  return () => {
    window.removeEventListener(JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME, handler);
  };
}

function getServerJornayaLeadId() {
  return null;
}

export async function waitForJornayaLeadId({
  timeoutMs = DEFAULT_WAIT_TIMEOUT_MS,
  intervalMs = DEFAULT_WAIT_INTERVAL_MS,
}: {
  timeoutMs?: number;
  intervalMs?: number;
} = {}): Promise<string | null> {
  const currentJornayaLeadId = getJornayaLeadId();
  if (currentJornayaLeadId || typeof window === "undefined" || timeoutMs <= 0) {
    return currentJornayaLeadId;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let interval: number | undefined;
    let timeout: number | undefined;

    const cleanup = () => {
      window.removeEventListener(JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME, check);
      if (interval !== undefined) window.clearInterval(interval);
      if (timeout !== undefined) window.clearTimeout(timeout);
    };

    const finish = (jornayaLeadId: string | null) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(jornayaLeadId);
    };

    const check = () => {
      const jornayaLeadId = getJornayaLeadId();
      if (jornayaLeadId) finish(jornayaLeadId);
    };

    window.addEventListener(JORNAYA_LEAD_ID_AVAILABLE_EVENT_NAME, check);
    interval = window.setInterval(check, intervalMs);
    timeout = window.setTimeout(() => finish(getJornayaLeadId()), timeoutMs);
  });
}

export const useJornaya = () => {
  const jornayaLeadId = useSyncExternalStore(
    subscribeToJornaya,
    getJornayaLeadId,
    getServerJornayaLeadId
  );

  const waitForLeadId = useCallback(
    (options?: Parameters<typeof waitForJornayaLeadId>[0]) => waitForJornayaLeadId(options),
    []
  );

  return {
    jornayaLeadId,
    waitForLeadId,
  };
};
